import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Webhook,
  Plus,
  Settings,
  Trash2,
  Play,
  Check,
  X,
  Copy,
  ExternalLink,
  Clock,
  Activity,
  AlertCircle,
  ChevronRight,
  Edit,
  Globe,
  Zap,
  Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { api, isUpgradeError } from '../context/AuthContext';

const CHANNEL_ICONS = [
  { value: 'webhook', label: 'Webhook', icon: Webhook },
  { value: 'globe', label: 'Globe', icon: Globe },
  { value: 'zap', label: 'Zap', icon: Zap },
  { value: 'send', label: 'Send', icon: Send },
];

const CHANNEL_COLORS = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#f97316', label: 'Orange' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#ef4444', label: 'Red' },
  { value: '#6b7280', label: 'Gray' },
];

const WebhooksPage = () => {
  const [activeTab, setActiveTab] = useState('channels');
  const [channels, setChannels] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChannelDialog, setShowChannelDialog] = useState(false);
  const [showWebhookDialog, setShowWebhookDialog] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [selectedWebhookForLogs, setSelectedWebhookForLogs] = useState(null);

  // Form states
  const [channelForm, setChannelForm] = useState({
    name: '',
    webhook_url: '',
    icon: 'webhook',
    color: '#6366f1',
    description: '',
    is_active: true
  });

  const [webhookForm, setWebhookForm] = useState({
    name: '',
    url: '',
    description: '',
    events: ['post.published'],
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [channelsRes, webhooksRes, eventsRes] = await Promise.all([
        api.get('/webhooks/channels/'),
        api.get('/webhooks/'),
        api.get('/webhooks/events')
      ]);
      setChannels(channelsRes.data);
      setWebhooks(webhooksRes.data);
      setEvents(eventsRes.data.events);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load webhooks data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChannel = async () => {
    try {
      if (editingChannel) {
        await api.put(`/webhooks/channels/${editingChannel.channel_id}`, channelForm);
        toast.success('Channel updated successfully');
      } else {
        await api.post('/webhooks/channels/', channelForm);
        toast.success('Channel created successfully');
      }
      setShowChannelDialog(false);
      resetChannelForm();
      fetchData();
    } catch (error) {
      if (!isUpgradeError(error)) toast.error(error.response?.data?.detail || 'Failed to save channel');
    }
  };

  const handleCreateWebhook = async () => {
    try {
      if (editingWebhook) {
        await api.put(`/webhooks/${editingWebhook.webhook_id}`, webhookForm);
        toast.success('Webhook updated successfully');
      } else {
        await api.post('/webhooks/', webhookForm);
        toast.success('Webhook created successfully');
      }
      setShowWebhookDialog(false);
      resetWebhookForm();
      fetchData();
    } catch (error) {
      if (!isUpgradeError(error)) toast.error(error.response?.data?.detail || 'Failed to save webhook');
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!confirm('Are you sure you want to delete this channel?')) return;
    try {
      await api.delete(`/webhooks/channels/${channelId}`);
      toast.success('Channel deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete channel');
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!confirm('Are you sure you want to delete this webhook?')) return;
    try {
      await api.delete(`/webhooks/${webhookId}`);
      toast.success('Webhook deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleTestChannel = async (channelId) => {
    try {
      await api.post(`/webhooks/channels/${channelId}/test`);
      toast.success('Test publish triggered');
    } catch (error) {
      toast.error('Failed to test channel');
    }
  };

  const handleTestWebhook = async (webhookId) => {
    try {
      await api.post(`/webhooks/${webhookId}/test`, {});
      toast.success('Test webhook triggered');
    } catch (error) {
      toast.error('Failed to test webhook');
    }
  };

  const handleViewLogs = async (webhookId) => {
    try {
      const response = await api.get(`/webhooks/${webhookId}/logs`);
      setWebhookLogs(response.data);
      setSelectedWebhookForLogs(webhookId);
    } catch (error) {
      toast.error('Failed to load logs');
    }
  };

  const editChannel = (channel) => {
    setEditingChannel(channel);
    setChannelForm({
      name: channel.name,
      webhook_url: channel.webhook_url,
      icon: channel.icon || 'webhook',
      color: channel.color || '#6366f1',
      description: channel.description || '',
      is_active: channel.is_active
    });
    setShowChannelDialog(true);
  };

  const editWebhook = (webhook) => {
    setEditingWebhook(webhook);
    setWebhookForm({
      name: webhook.name,
      url: webhook.url,
      description: webhook.description || '',
      events: webhook.events || ['post.published'],
      is_active: webhook.is_active
    });
    setShowWebhookDialog(true);
  };

  const resetChannelForm = () => {
    setEditingChannel(null);
    setChannelForm({
      name: '',
      webhook_url: '',
      icon: 'webhook',
      color: '#6366f1',
      description: '',
      is_active: true
    });
  };

  const resetWebhookForm = () => {
    setEditingWebhook(null);
    setWebhookForm({
      name: '',
      url: '',
      description: '',
      events: ['post.published'],
      is_active: true
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'success') return <Badge className="bg-green-100 text-green-700">Success</Badge>;
    if (status === 'failed') return <Badge className="bg-red-100 text-red-700">Failed</Badge>;
    if (status === 'error') return <Badge className="bg-red-100 text-red-700">Error</Badge>;
    return <Badge variant="secondary">Never triggered</Badge>;
  };

  const IconComponent = ({ iconName, className }) => {
    const iconData = CHANNEL_ICONS.find(i => i.value === iconName);
    if (iconData) {
      const Icon = iconData.icon;
      return <Icon className={className} />;
    }
    return <Webhook className={className} />;
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Custom Channels & Webhooks</h1>
            <p className="text-slate-600 mt-1">
              Create custom publishing channels and event webhooks
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="channels" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Custom Channels
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="flex items-center gap-2">
              <Webhook className="w-4 h-4" />
              Webhooks
            </TabsTrigger>
          </TabsList>

          {/* Custom Channels Tab */}
          <TabsContent value="channels" className="space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-slate-600">
                Custom channels let you publish posts to any platform via webhooks
              </p>
              <Button
                onClick={() => {
                  resetChannelForm();
                  setShowChannelDialog(true);
                }}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="add-channel-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Channel
              </Button>
            </div>

            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-12 w-12 bg-slate-200 rounded-xl mb-4" />
                      <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : channels.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-12 text-center">
                  <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No custom channels yet</h3>
                  <p className="text-slate-600 mb-4">
                    Create a custom channel to publish posts to any platform via webhook
                  </p>
                  <Button onClick={() => setShowChannelDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Channel
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {channels.map(channel => (
                  <motion.div
                    key={channel.channel_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${channel.color}20` }}
                          >
                            <IconComponent
                              iconName={channel.icon}
                              className="w-6 h-6"
                              style={{ color: channel.color }}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={channel.is_active}
                              onCheckedChange={async (checked) => {
                                await api.put(`/webhooks/channels/${channel.channel_id}`, {
                                  is_active: checked
                                });
                                fetchData();
                              }}
                            />
                          </div>
                        </div>
                        
                        <h3 className="font-semibold text-slate-900 mb-1">{channel.name}</h3>
                        <p className="text-sm text-slate-500 mb-4 truncate">{channel.webhook_url}</p>
                        
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 text-slate-500">
                            <Activity className="w-4 h-4" />
                            <span>{channel.posts_published || 0} posts</span>
                          </div>
                          {getStatusBadge(channel.last_status)}
                        </div>
                        
                        <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTestChannel(channel.channel_id)}
                            data-testid={`test-channel-${channel.channel_id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Test
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => editChannel(channel)}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteChannel(channel.channel_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Webhooks Tab */}
          <TabsContent value="webhooks" className="space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-slate-600">
                Webhooks notify external services when events occur in StelliSocial
              </p>
              <Button
                onClick={() => {
                  resetWebhookForm();
                  setShowWebhookDialog(true);
                }}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="add-webhook-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Webhook
              </Button>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-4 bg-slate-200 rounded w-1/4 mb-2" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : webhooks.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-12 text-center">
                  <Webhook className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No webhooks yet</h3>
                  <p className="text-slate-600 mb-4">
                    Create a webhook to receive notifications when events occur
                  </p>
                  <Button onClick={() => setShowWebhookDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Webhook
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {webhooks.map(webhook => (
                  <motion.div
                    key={webhook.webhook_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-slate-900">{webhook.name}</h3>
                              {getStatusBadge(webhook.last_status)}
                              <Switch
                                checked={webhook.is_active}
                                onCheckedChange={async (checked) => {
                                  await api.put(`/webhooks/${webhook.webhook_id}`, {
                                    is_active: checked
                                  });
                                  fetchData();
                                }}
                              />
                            </div>
                            <p className="text-sm text-slate-500 mb-2 font-mono">{webhook.url}</p>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {webhook.events?.map(event => (
                                <Badge key={event} variant="secondary" className="text-xs">
                                  {event}
                                </Badge>
                              ))}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Activity className="w-4 h-4" />
                                {webhook.trigger_count || 0} triggers
                              </span>
                              {webhook.last_triggered && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  Last: {new Date(webhook.last_triggered).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleTestWebhook(webhook.webhook_id)}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Test
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewLogs(webhook.webhook_id)}
                            >
                              <Clock className="w-4 h-4 mr-1" />
                              Logs
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => editWebhook(webhook)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={() => handleDeleteWebhook(webhook.webhook_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Webhook Logs */}
                        {selectedWebhookForLogs === webhook.webhook_id && webhookLogs.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <h4 className="text-sm font-medium text-slate-700 mb-3">Recent Logs</h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {webhookLogs.slice(0, 5).map(log => (
                                <div
                                  key={log.log_id}
                                  className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm"
                                >
                                  <div className="flex items-center gap-2">
                                    {log.status === 'success' ? (
                                      <Check className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <X className="w-4 h-4 text-red-500" />
                                    )}
                                    <span className="text-slate-600">
                                      {new Date(log.triggered_at).toLocaleString()}
                                    </span>
                                    {log.is_test && <Badge variant="secondary">Test</Badge>}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-slate-500">{log.response_code}</span>
                                    <span className="text-slate-400">{log.duration_ms}ms</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create/Edit Channel Dialog */}
        <Dialog open={showChannelDialog} onOpenChange={setShowChannelDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingChannel ? 'Edit Channel' : 'Create Custom Channel'}</DialogTitle>
              <DialogDescription>
                Custom channels publish posts to external platforms via webhooks
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Channel Name</label>
                <Input
                  value={channelForm.name}
                  onChange={(e) => setChannelForm({ ...channelForm, name: e.target.value })}
                  placeholder="e.g., My Blog, Discord, Slack"
                  data-testid="channel-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Webhook URL</label>
                <Input
                  value={channelForm.webhook_url}
                  onChange={(e) => setChannelForm({ ...channelForm, webhook_url: e.target.value })}
                  placeholder="https://your-webhook-url.com/publish"
                  data-testid="channel-url-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Icon</label>
                  <Select
                    value={channelForm.icon}
                    onValueChange={(value) => setChannelForm({ ...channelForm, icon: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHANNEL_ICONS.map(icon => (
                        <SelectItem key={icon.value} value={icon.value}>
                          {icon.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Color</label>
                  <Select
                    value={channelForm.color}
                    onValueChange={(value) => setChannelForm({ ...channelForm, color: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHANNEL_COLORS.map(color => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: color.value }}
                            />
                            {color.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Description (optional)</label>
                <Textarea
                  value={channelForm.description}
                  onChange={(e) => setChannelForm({ ...channelForm, description: e.target.value })}
                  placeholder="Describe this channel..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowChannelDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateChannel}
                disabled={!channelForm.name || !channelForm.webhook_url}
                data-testid="save-channel-btn"
              >
                {editingChannel ? 'Update' : 'Create'} Channel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Webhook Dialog */}
        <Dialog open={showWebhookDialog} onOpenChange={setShowWebhookDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingWebhook ? 'Edit Webhook' : 'Create Webhook'}</DialogTitle>
              <DialogDescription>
                Webhooks send notifications to your URL when events occur
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Webhook Name</label>
                <Input
                  value={webhookForm.name}
                  onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })}
                  placeholder="e.g., Slack Notifications"
                  data-testid="webhook-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Webhook URL</label>
                <Input
                  value={webhookForm.url}
                  onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                  placeholder="https://your-webhook-url.com/events"
                  data-testid="webhook-url-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Events</label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {events.map(event => (
                    <label
                      key={event.event}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={webhookForm.events.includes(event.event)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setWebhookForm({
                              ...webhookForm,
                              events: [...webhookForm.events, event.event]
                            });
                          } else {
                            setWebhookForm({
                              ...webhookForm,
                              events: webhookForm.events.filter(ev => ev !== event.event)
                            });
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="text-slate-600">{event.event}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Description (optional)</label>
                <Textarea
                  value={webhookForm.description}
                  onChange={(e) => setWebhookForm({ ...webhookForm, description: e.target.value })}
                  placeholder="Describe this webhook..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowWebhookDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateWebhook}
                disabled={!webhookForm.name || !webhookForm.url || webhookForm.events.length === 0}
                data-testid="save-webhook-btn"
              >
                {editingWebhook ? 'Update' : 'Create'} Webhook
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default WebhooksPage;
